import { useState, useEffect } from "react";
import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import CustomTable from "../../Components/CustomTable";
import CustomPagination from "../../Components/CustomPagination";
import CustomModal from "../../Components/CustomModal";
import { toast } from "react-toastify";
import "./reviews.css";
import { useErrorBoundary } from "../../Hooks/useErrorBoundary";

export const ReviewManagement = () => {
  const [reviews, setReviews] = useState([]);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedReview, setSelectedReview] = useState(null);
  const [statusToUpdate, setStatusToUpdate] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const token = localStorage.getItem("login");
  const apiUrl = process.env.REACT_APP_BASE_URL;

  // ✅ Error Boundary Hook
  const { error, handleError } = useErrorBoundary();

  // 🔥 Trigger ErrorBoundary (IMPORTANT)
  if (error) {
    throw error.error;
  }

  useEffect(() => {
    fetchReviews();
  }, []);

  const safeParse = async (res) => {
    const contentType = res.headers.get("content-type");

    if (!contentType) {
      throw new Error("No content-type received from server");
    }

    if (contentType.includes("application/json")) {
      return await res.json();
    }

    throw new Error(`Invalid response format: ${contentType}`);
  };

  const fetchReviews = (page = 1) => {
    document.querySelector(".loaderBox")?.classList.remove("d-none");

    fetch(`${apiUrl}/admin/reviews?page=${page}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return await safeParse(res);
      })
      .then((data) => {
        document.querySelector(".loaderBox")?.classList.add("d-none");

        if (data?.status) {
          setReviews(data.data.data || []);
          setPagination(data.data || {});
          setCurrentPage(data.data.current_page || 1);
        } else {
          throw new Error(data?.message || "Failed to fetch reviews");
        }
      })
      .catch((err) => {
        document.querySelector(".loaderBox")?.classList.add("d-none");

        console.error("[ReviewManagement] Fetch Error:", err);
        toast.error("Failed to fetch reviews");

        // ✅ Send to ErrorBoundary
        handleError(err, {
          component: "ReviewManagement",
          action: "fetchReviews",
          context: "api_failure",
        });
      });
  };

  const handleStatusChange = (reviewId, status) => {
    if (!reviewId || status === null) {
      toast.error("Invalid action");
      return;
    }

    setSelectedReview(reviewId);
    setStatusToUpdate(status);
    setShowModal(true);
  };

  const confirmStatusUpdate = () => {
    // ✅ validation
    if (!selectedReview) {
      toast.error("No review selected");
      return;
    }

    if (statusToUpdate === null) {
      toast.error("Invalid status");
      return;
    }

    document.querySelector(".loaderBox")?.classList.remove("d-none");

    fetch(`${apiUrl}/admin/review-status/${selectedReview}`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        status: statusToUpdate,
      }),
    })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return await safeParse(res);
      })
      .then((data) => {
        document.querySelector(".loaderBox")?.classList.add("d-none");

        if (data?.status) {
          toast.success("Review status updated successfully");

          setShowModal(false);
          fetchReviews(currentPage);
        } else {
          throw new Error(data?.message || "Something went wrong");
        }
      })
      .catch((err) => {
        document.querySelector(".loaderBox")?.classList.add("d-none");

        console.error("[ReviewManagement] Update Error:", err);
        toast.error("Failed to update review status");

        // ✅ Send to ErrorBoundary
        handleError(err, {
          component: "ReviewManagement",
          action: "confirmStatusUpdate",
          context: "update_failure",
          reviewId: selectedReview,
        });
      });
  };

  const headers = [
    { key: "id", title: "S.No" },
    { key: "name", title: "Name" },
    { key: "rating", title: "Rating" },
    { key: "review", title: "Review" },
    { key: "status", title: "Status" },
    { key: "action", title: "Action" },
  ];

  const renderStatus = (status) => {
    if (status === 0) return <span>Pending</span>;
    if (status === 1) return <span>Approved</span>;
    if (status === 2) return <span>Rejected</span>;
    return <span>-</span>; // ✅ fallback
  };

  return (
    <DashboardLayout>
      <div className="dashCard reviewPage">
        <h2 className="mainTitle">Review Management</h2>

        <CustomTable headers={headers}>
          <tbody>
            {reviews?.length > 0 ? (
              reviews.map((item, index) => (
                <tr key={item.id}>
                  <td>{index + 1}</td>

                  <td className="reviewName">{item.name || "-"}</td>

                  <td>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span
                        key={star}
                        className={
                          star <= item.rating
                            ? "starActive"
                            : "starInactive"
                        }
                      >
                        ★
                      </span>
                    ))}
                  </td>

                  <td className="reviewText reviewColumn">
                    {item.review || "-"}
                  </td>

                  <td>{renderStatus(item.status)}</td>

                  <td>
                    {item.status === 0 && (
                      <div className="actionWrapper">
                        <button
                          className="actionBtn"
                          onClick={() =>
                            handleStatusChange(item.id, 1)
                          }
                        >
                          Approve
                        </button>

                        <button
                          className="actionBtn"
                          onClick={() =>
                            handleStatusChange(item.id, 2)
                          }
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="text-center">
                  No reviews found
                </td>
              </tr>
            )}
          </tbody>
        </CustomTable>

        {pagination?.last_page > 1 && (
          <CustomPagination
            itemsPerPage={pagination.per_page}
            totalItems={pagination.total}
            currentPage={currentPage}
            onPageChange={(page) => fetchReviews(page)}
          />
        )}

        <CustomModal
          show={showModal}
          close={() => setShowModal(false)}
          action={confirmStatusUpdate}
          heading="Are you sure you want to update this review status?"
        />
      </div>
    </DashboardLayout>
  );
};